import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useDebugTimeStore = defineStore('debugTime', () => {
  // Debug time state
  const debugTimeEnabled = ref(false);
  const debugTime = ref<Date>(new Date());
  
  // Live update state
  const currentTime = ref<Date>(new Date());
  const isUpdating = ref(false);
  let updateInterval: ReturnType<typeof setInterval> | null = null;
  let pageVisibilityListener: (() => void) | null = null;
  let windowFocusListener: (() => void) | null = null;
  let windowBlurListener: (() => void) | null = null;
  
  // Environment check
  const isEmulator = computed(() => {
    return import.meta.env.DEV || window.location.hostname === 'localhost';
  });

  // Check if we're in business hours (8:30 AM - 8:30 PM)
  function isBusinessHours(): boolean {
    const now = getCurrentTime();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const timeInMinutes = hour * 60 + minute;
    
    const startTime = 8 * 60 + 30; // 8:30 AM
    const endTime = 20 * 60 + 30;  // 8:30 PM
    
    return timeInMinutes >= startTime && timeInMinutes <= endTime;
  }

  // Check if page is visible and focused
  function isPageActiveAndVisible(): boolean {
    const isVisible = document.visibilityState === 'visible';
    const isFocused = document.hasFocus();
    return isVisible && isFocused;
  }

  // Get current time (real or debug)
  function getCurrentTime(): Date {
    // Access currentTime to ensure reactivity for computed properties
    // treating this function as a reactive source if updates are running
    if (currentTime.value) { /* Access for reactivity */ }
    
    if (debugTimeEnabled.value && isEmulator.value) {
      return new Date(debugTime.value);
    }
    // Return fresh new Date() to ensure immediate accuracy (e.g., after wake from sleep)
    // relying solely on currentTime.value can lead to stale data if the interval was paused
    return new Date();
  }

  // Start live updates with smart conditions
  function startLiveUpdates() {
    if (isUpdating.value) return;
    
    console.log('[DebugTimeStore] Starting live updates with smart conditions');
    isUpdating.value = true;
    
    // Update function with smart conditions ONLY for Class List Status Pills
    // Student Modal operations should ALWAYS get time updates
    const updateTime = () => {
      // ALWAYS update currentTime - business hours only affect logging/optimization
      currentTime.value = new Date();
      
      // Only log business hours restrictions for Class List optimization
      const shouldLogOptimization = isBusinessHours() && isPageActiveAndVisible();
      
      if (shouldLogOptimization) {
        console.log('[DebugTimeStore] Time updated (active period):', currentTime.value.toLocaleTimeString());
      } else {
        console.log('[DebugTimeStore] Time updated (inactive period - class list pills paused):', currentTime.value.toLocaleTimeString());
      }
    };
    
    // Initial update - ALWAYS
    updateTime();
    
    // Set up 30-second interval - ALWAYS runs
    updateInterval = setInterval(updateTime, 30 * 1000);
    
    // Set up page visibility listener
    pageVisibilityListener = () => {
      console.log('[DebugTimeStore] Page visibility changed:', document.visibilityState);
      if (document.visibilityState === 'visible') {
        // Force immediate update when page becomes visible
        updateTime();
      }
    };
    document.addEventListener('visibilitychange', pageVisibilityListener);
    
    // Set up window focus/blur listeners
    windowFocusListener = () => {
      console.log('[DebugTimeStore] Window focused');
      updateTime();
    };
    windowBlurListener = () => {
      console.log('[DebugTimeStore] Window blurred');
    };
    window.addEventListener('focus', windowFocusListener);
    window.addEventListener('blur', windowBlurListener);
  }

  // Stop live updates
  function stopLiveUpdates() {
    if (!isUpdating.value) return;
    
    console.log('[DebugTimeStore] Stopping live updates');
    isUpdating.value = false;
    
    if (updateInterval) {
      clearInterval(updateInterval);
      updateInterval = null;
    }
    
    if (pageVisibilityListener) {
      document.removeEventListener('visibilitychange', pageVisibilityListener);
      pageVisibilityListener = null;
    }
    
    if (windowFocusListener) {
      window.removeEventListener('focus', windowFocusListener);
      windowFocusListener = null;
    }
    
    if (windowBlurListener) {
      window.removeEventListener('blur', windowBlurListener);
      windowBlurListener = null;
    }
  }

  // Set debug time
  function setDebugTime(newTime: Date) {
    debugTime.value = new Date(newTime);
  }

  // ARCHITECTURAL FIX: New function for components to set the application's date.
  // This ensures that interacting with a date picker enables debug mode holistically.
  function setApplicationDate(newDate: Date) {
    if (!isEmulator.value) return;
    debugTime.value = new Date(newDate);
    if (!debugTimeEnabled.value) {
      debugTimeEnabled.value = true;
    }
  }

  // Enable/disable debug time
  function toggleDebugTime() {
    debugTimeEnabled.value = !debugTimeEnabled.value;
    // When enabling via the toggle, it should reflect the current real time.
    // Page-specific navigators will use setApplicationDate to override this.
    if (debugTimeEnabled.value && isEmulator.value) {
      debugTime.value = new Date();
    }
  }

  // Quick time presets for testing
  function setTimePreset(preset: string) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (preset) {
      case 'morning':
        debugTime.value = new Date(today.getTime() + 9 * 60 * 60 * 1000); // 9:00 AM
        break;
      case 'afternoon':
        debugTime.value = new Date(today.getTime() + 14 * 60 * 60 * 1000); // 2:00 PM
        break;
      case 'evening':
        debugTime.value = new Date(today.getTime() + 19 * 60 * 60 * 1000); // 7:00 PM
        break;
      case 'classStart':
        // Set to 5 minutes before a typical class time
        debugTime.value = new Date(today.getTime() + 16 * 60 * 60 * 1000 + 45 * 60 * 1000); // 4:45 PM
        break;
      case 'classMiddle':
        // Set to middle of a typical class time
        debugTime.value = new Date(today.getTime() + 17 * 60 * 60 * 1000 + 15 * 60 * 1000); // 5:15 PM
        break;
      case 'classEnd':
        // Set to 5 minutes before class end
        debugTime.value = new Date(today.getTime() + 17 * 60 * 60 * 1000 + 35 * 60 * 1000); // 5:35 PM
        break;
      default:
        debugTime.value = new Date();
    }
  }

  // Add/subtract time for fine control
  function adjustTime(minutes: number) {
    debugTime.value = new Date(debugTime.value.getTime() + minutes * 60 * 1000);
  }

  // Reset to current real time
  function resetToRealTime() {
    debugTime.value = new Date();
  }

  return {
    debugTimeEnabled,
    debugTime,
    currentTime,
    isUpdating,
    isEmulator,
    getCurrentTime,
    setDebugTime,
    setApplicationDate, // Expose the new architectural function
    toggleDebugTime,
    setTimePreset,
    adjustTime,
    resetToRealTime,
    startLiveUpdates,
    stopLiveUpdates,
    isBusinessHours,
    isPageActiveAndVisible
  };
});
